import React, { useEffect, useState } from "react";
import { getDataWithFilterUrl } from "../../controllers/APIController";
import {
  Table,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableColumn,
  Button,
  useDisclosure,
  Image,
  Pagination,
  Card,
  CardBody,
  Chip,
  Avatar,
  Badge,
} from "@nextui-org/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faEye,
  faComments,
  faCalendar,
  faUser,
  faBuilding,
  faCheckCircle,
  faExclamationTriangle,
  faClock,
  faPlus,
  faFilter,
  faSearch,
  faStar,
  faThumbsUp,
  faThumbsDown,
  faFaceMeh,
} from "@fortawesome/free-solid-svg-icons";
import DownloadIcon from "../../Images/Icons/download.svg";

import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import NullData from "../../components/ErrorPage/NullData";
import LoadingPage from "../../components/Loader/LoadingPage";
import AddEvidenceModal from "./AddEvidenceModal";
import { StandardPrimaryButton, StandardSecondaryButton } from "../../components/StandardButtons";

const StakeholderFeedback = () => {
  const [reRenderPage, setReRenderPage] = useState(true);
  const [filterEvidenceData, setFilterEvidenceData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  const UserLogged = JSON.parse(sessionStorage.getItem("userData"));
  const OrgInfo = UserLogged && UserLogged.user.LastOrgId;
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + OrgInfo;

  // Mock data for stakeholder feedback
  const mockStakeholderFeedback = [
    {
      id: 1,
      title: "Customer Satisfaction Survey Q4 2024",
      description: "Quarterly customer feedback on sustainability initiatives and product environmental impact",
      stakeholderType: "Customers",
      feedbackType: "Survey",
      sentiment: "positive",
      rating: 4.2,
      totalResponses: 1250,
      keyInsights: 8,
      submissionDate: "2024-12-20",
      status: "analyzed",
      evidenceUrl: "#",
    },
    {
      id: 2,
      title: "Employee ESG Engagement Feedback",
      description: "Internal feedback on ESG initiatives, training programs, and sustainability culture",
      stakeholderType: "Employees",
      feedbackType: "Internal Survey",
      sentiment: "positive",
      rating: 3.8,
      totalResponses: 450,
      keyInsights: 12,
      submissionDate: "2024-12-15",
      status: "in-review",
      evidenceUrl: "#",
    },
    {
      id: 3,
      title: "Investor ESG Performance Review",
      description: "Investor feedback on ESG performance, transparency, and sustainability reporting",
      stakeholderType: "Investors",
      feedbackType: "Review",
      sentiment: "neutral",
      rating: 3.5,
      totalResponses: 25,
      keyInsights: 5,
      submissionDate: "2024-12-10",
      status: "analyzed",
      evidenceUrl: "#",
    },
    {
      id: 4,
      title: "Supplier Sustainability Assessment",
      description: "Feedback from suppliers on sustainability requirements and collaboration opportunities",
      stakeholderType: "Suppliers",
      feedbackType: "Assessment",
      sentiment: "positive",
      rating: 4.0,
      totalResponses: 180,
      keyInsights: 9,
      submissionDate: "2024-12-05",
      status: "pending",
      evidenceUrl: "#",
    },
    {
      id: 5,
      title: "Community Impact Feedback",
      description: "Local community feedback on social impact initiatives and community engagement programs",
      stakeholderType: "Community",
      feedbackType: "Community Survey",
      sentiment: "positive",
      rating: 4.5,
      totalResponses: 320,
      keyInsights: 15,
      submissionDate: "2024-11-30",
      status: "analyzed",
      evidenceUrl: "#",
    },
    {
      id: 6,
      title: "Regulatory Compliance Feedback",
      description: "Feedback from regulatory bodies on ESG compliance and reporting standards",
      stakeholderType: "Regulators",
      feedbackType: "Compliance Review",
      sentiment: "neutral",
      rating: 3.2,
      totalResponses: 8,
      keyInsights: 3,
      submissionDate: "2024-11-25",
      status: "analyzed",
      evidenceUrl: "#",
    },
  ];

  const downloadHandler = (url) => {
    window.open(url);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "analyzed":
        return "success";
      case "in-review":
        return "primary";
      case "pending":
        return "warning";
      case "overdue":
        return "danger";
      default:
        return "default";
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case "positive":
        return "success";
      case "neutral":
        return "warning";
      case "negative":
        return "danger";
      default:
        return "default";
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case "positive":
        return faThumbsUp;
      case "neutral":
        return faFaceMeh;
      case "negative":
        return faThumbsDown;
      default:
        return faFaceMeh;
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <FontAwesomeIcon 
          key={i} 
          icon={faStar} 
          className="text-yellow-400 text-xs" 
        />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <FontAwesomeIcon 
          key="half" 
          icon={faStar} 
          className="text-yellow-400 text-xs opacity-50" 
        />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <FontAwesomeIcon 
          key={`empty-${i}`} 
          icon={faStar} 
          className="text-neutral-300 text-xs" 
        />
      );
    }

    return stars;
  };

  useEffect(() => {
    // Simulate API call with mock data
    setTimeout(() => {
      setFilterEvidenceData(mockStakeholderFeedback);
      setIsLoading(false);
    }, 1000);
  }, [reRenderPage, orgFilterUrl]);

  const ItemPerPage = 10;
  const totalPage = Math.ceil(filterEvidenceData?.length / ItemPerPage);

  const summaryStats = [
    {
      title: "Total Feedback",
      value: filterEvidenceData.length,
      icon: faComments,
      color: "primary",
              bgColor: "bg-emerald-50",
      textColor: "text-primary-700",
    },
    {
      title: "Positive Sentiment",
      value: filterEvidenceData.filter(item => item.sentiment === "positive").length,
      icon: faThumbsUp,
      color: "success",
      bgColor: "bg-success-50",
      textColor: "text-success-700",
    },
    {
      title: "Avg Rating",
      value: (filterEvidenceData.reduce((acc, item) => acc + item.rating, 0) / filterEvidenceData.length).toFixed(1),
      icon: faStar,
      color: "warning",
      bgColor: "bg-yellow-50",
      textColor: "text-yellow-700",
    },
    {
      title: "Total Responses",
      value: filterEvidenceData.reduce((acc, item) => acc + item.totalResponses, 0),
      icon: faUser,
      color: "secondary",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Page Header */}
      <div className="bg-white border-b border-neutral-200 px-8 py-6 shadow-sm">
        <div className="flex items-center justify-between max-w-full">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900">Stakeholder Feedback</h1>
            <p className="text-sm text-neutral-600 mt-1">Collect and manage feedback from various stakeholders on ESG initiatives</p>
          </div>
          <StandardPrimaryButton
            className="px-6 font-medium"
            startContent={<FontAwesomeIcon icon={faPlus} className="text-sm" />}
            onClick={() => onOpen()}
          >
            Add Feedback
          </StandardPrimaryButton>
        </div>
      </div>

      <BreadCrumbsPage>
        <div className="max-w-7xl mx-auto px-8 py-8">
          <LoadingPage isLoading={isLoading} loadingText="Loading stakeholder feedback...">
            {/* Summary Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {summaryStats.map((stat, index) => (
                <Card key={index} className="border border-neutral-200 shadow-sm">
                  <CardBody className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                        <FontAwesomeIcon 
                          icon={stat.icon} 
                          className={`text-lg ${stat.textColor}`} 
                        />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-neutral-600">{stat.title}</p>
                        <p className={`text-lg font-semibold ${stat.textColor}`}>
                          {stat.value}
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>

            {/* Stakeholder Feedback Management Section */}
            <div className="bg-white rounded-lg border border-neutral-200 shadow-sm p-6 mb-8">
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">Stakeholder Feedback Management</h2>
                <p className="text-sm text-neutral-600 mt-1">
                  Collect, analyze, and manage feedback from customers, employees, investors, suppliers, and other stakeholders. Use this data to improve ESG performance and stakeholder relationships.
                </p>
              </div>
            </div>

            {/* Stakeholder Feedback Repository Section */}
            <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-neutral-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900">Stakeholder Feedback Repository</h3>
                    <p className="text-sm text-neutral-600 mt-1">Browse and manage stakeholder feedback and insights</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StandardSecondaryButton
                      size="sm"
                      startContent={<FontAwesomeIcon icon={faSearch} className="text-xs" />}
                      className="border-neutral-300 hover:border-neutral-400"
                    >
                      Search
                    </StandardSecondaryButton>
                    <StandardSecondaryButton
                      size="sm"
                      startContent={<FontAwesomeIcon icon={faFilter} className="text-xs" />}
                      className="border-neutral-300 hover:border-neutral-400"
                    >
                      Filter
                    </StandardSecondaryButton>
                  </div>
                </div>
              </div>
              <div className="p-6">
                {filterEvidenceData && filterEvidenceData?.length > 0 ? (
                  <>
                    <Table
                      removeWrapper
                      aria-label="Stakeholder feedback table"
                      classNames={{
                        wrapper: "shadow-none",
                        th: "bg-neutral-50 text-neutral-700 font-semibold text-sm border-b border-neutral-200",
                        td: "border-b border-neutral-100",
                      }}
                    >
                      <TableHeader>
                        <TableColumn>Feedback Title</TableColumn>
                        <TableColumn>Stakeholder Type</TableColumn>
                        <TableColumn>Sentiment</TableColumn>
                        <TableColumn>Rating</TableColumn>
                        <TableColumn>Responses</TableColumn>
                        <TableColumn>Status</TableColumn>
                        <TableColumn>Submission Date</TableColumn>
                        <TableColumn>Actions</TableColumn>
                      </TableHeader>
                      <TableBody
                        emptyContent={<NullData title="No stakeholder feedback found." />}
                      >
                        {filterEvidenceData &&
                          Array.isArray(filterEvidenceData) &&
                          filterEvidenceData.map((item, index) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                <div className="flex flex-col gap-1">
                                  <div className="flex items-center gap-2">
                                    <FontAwesomeIcon 
                                      icon={faComments} 
                                      className="text-neutral-500 text-sm" 
                                    />
                                    <span className="text-sm font-medium text-neutral-900">
                                      {item.title}
                                    </span>
                                  </div>
                                  <span className="text-xs text-neutral-500 max-w-xs truncate block">
                                    {item.description}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-1">
                                  <Chip size="sm" variant="flat" color="secondary">
                                    {item.stakeholderType}
                                  </Chip>
                                  <span className="text-xs text-neutral-500">{item.feedbackType}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <FontAwesomeIcon 
                                    icon={getSentimentIcon(item.sentiment)} 
                                    className={`text-xs ${getSentimentColor(item.sentiment) === "success" ? "text-success-600" : getSentimentColor(item.sentiment) === "warning" ? "text-yellow-600" : "text-error-600"}`} 
                                  />
                                  <Chip
                                    size="sm"
                                    variant="flat"
                                    color={getSentimentColor(item.sentiment)}
                                  >
                                    {item.sentiment.charAt(0).toUpperCase() + item.sentiment.slice(1)}
                                  </Chip>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-1">
                                  <div className="flex items-center gap-1">
                                    {renderStars(item.rating)}
                                  </div>
                                  <span className="text-xs text-neutral-600">{item.rating}/5</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-1">
                                  <span className="text-sm font-semibold text-neutral-900">
                                    {item.totalResponses.toLocaleString()}
                                  </span>
                                  <span className="text-xs text-neutral-500">
                                    {item.keyInsights} insights
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="sm"
                                  variant="flat"
                                  color={getStatusColor(item.status)}
                                >
                                  {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                </Chip>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <FontAwesomeIcon 
                                    icon={faCalendar} 
                                    className="text-neutral-500 text-xs" 
                                  />
                                  <span className="text-sm text-neutral-600">
                                    {new Date(item.submissionDate).toLocaleDateString()}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <StandardSecondaryButton
                                    size="sm"
                                    className="text-neutral-600 hover:text-neutral-900"
                                    startContent={<FontAwesomeIcon icon={faEye} className="text-xs" />}
                                  >
                                    View
                                  </StandardSecondaryButton>
                                  <StandardSecondaryButton
                                    size="sm"
                                    className="text-neutral-600 hover:text-neutral-900"
                                    startContent={<FontAwesomeIcon icon={faDownload} className="text-xs" />}
                                    onClick={() => downloadHandler(item.evidenceUrl)}
                                  >
                                    Download
                                  </StandardSecondaryButton>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                    {filterEvidenceData?.length > 0 && (
                      <div className="w-full mt-8 flex items-center justify-center">
                        <Pagination
                          showControls
                          total={totalPage}
                          color="secondary"
                          page={currentPage}
                          onChange={setCurrentPage}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <NullData />
                )}
              </div>
            </div>
          </LoadingPage>
        </div>
      </BreadCrumbsPage>

      <AddEvidenceModal
        onSubmitHandler={1}
        id={1}
        isOpen={isOpen}
        onOpen={onOpen}
        onOpenChange={onOpenChange}
        setReRenderPage={setReRenderPage}
      />
    </div>
  );
};

export default StakeholderFeedback; 